import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Modal } from "semantic-ui-react";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import NumberField from "../../components/Editing/NumberField";
import DataSections from "../Lift/IOEnumerations/IoDataSections/DataSections";
import ActionMenuButton from "../../components/ActionMenuButton/ActionMenuButton";

import add from "../../images/circle-plus.svg";
import close from "../../images/crossGray.svg";

type StateProps = {
  editedEnumeration: Api.IoBasicDataType;
  storedEnumeration: Api.IoBasicDataType;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi;
};

export type ModalState = {
  isModalOpen: boolean;
};

const baseEnumFields = nameofFactory<Api.IoBasicDataType>();

export type EditIoBasicDataTypeProps = StateProps & DispatchProps & OwnProps;

export class EditIoBasicDataType extends React.Component<EditIoBasicDataTypeProps, ModalState> {
  constructor(props: EditIoBasicDataTypeProps) {
    super(props);
    this.state = {
      isModalOpen: false
    };
  }
  render(): JSX.Element {
    const {
      editedEnumeration,
      storedEnumeration,
      activeUser,
      toggleModal,
      dataFieldName
    } = this.props;
    const { isModalOpen } = this.state;
    const isNew = !storedEnumeration;
    const usedDatasection =
      editedEnumeration && editedEnumeration.iodataSections ? editedEnumeration.iodataSections : [];

    const uniqueDatasections = _.differenceBy(
      this.props.pdApi.iodataSections,
      usedDatasection,
      "id"
    );
    if(storedEnumeration){
      storedEnumeration.comment = storedEnumeration.comment ? storedEnumeration.comment : "";
    }
    const permission = hasWriteAccess(activeUser, "Lift");
    return (
      <Editor
        itemName={"Enumeration"}
        originalObject={storedEnumeration}
        editedObject={editedEnumeration}
        hasWritePermission={permission}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update(dataFieldName as Api.OptionsGrud)}
        create={() => this.props.editActions.add(dataFieldName as Api.OptionsGrud)}
        remove={{ callback: toggleModal }}
      >
        <NumberField
          id="enumValue"
          label="Value"
          value={editedEnumeration ? editedEnumeration.enumValue : 0}
          isError={editedEnumeration && !this.isFieldValid(baseEnumFields("enumValue"))}
          errorMessage="The value must be unique."
          readonly={!isNew}
          onChange={enumValue =>
            this.props.editActions.setEdited({ ...editedEnumeration, enumValue: enumValue })
          }
        />
        <TextField
          id="enumName"
          label="Enumeration Name"
          value={editedEnumeration ? editedEnumeration.enumName : ""}
          isError={editedEnumeration && !this.isFieldValid(baseEnumFields("enumName"))}
          errorMessage={!this.isEmptyString(editedEnumeration.enumName) ? "The enumeration name must be unique." : ""}
          onChange={enumName =>
            this.props.editActions.setEdited({ ...editedEnumeration, enumName: enumName.trim() })
          }
          readonly={!permission}
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedEnumeration ? editedEnumeration.comment : ""}
          isError={editedEnumeration && !this.isFieldValid(baseEnumFields("comment"))}
          onChange={comment =>
            this.props.editActions.setEdited({ ...editedEnumeration, comment: comment.trim() })
          }
          readonly={!permission}
        />
        <div className="add-data-sections-wrapper">
          {editedEnumeration && editedEnumeration.iodataSections.length > 0 && (
            <DataSections
              isRowSelected={() => false}
              handleRowSelection={() => null}
              rows={usedDatasection}
              columnType={permission ? "IO Data Sections With Remove" : "IO Data Sections"}
            />
          )}
          {hasWriteAccess(activeUser, "Lift") && (
            <ActionMenuButton id="AddDataSection" onClick={this.toggleModal}>
              <div id="addDataSectionIcon">
                <img src={add} alt="Add data section" />
                <span>Add data section</span>
              </div>
            </ActionMenuButton>
          )}
        </div>

        <Modal size="large" open={isModalOpen} className="data-sections-modal">
          <div className="modal-header">
            <p className="title">Select data section to add</p>
            <img
              id="closeModal"
              width="18"
              height="18"
              className="close-icon"
              src={close}
              alt="Close Icon"
              onClick={this.toggleModal}
            />
          </div>
          <Modal.Content scrolling={true}>
            <DataSections
              isRowSelected={() => false}
              handleRowSelection={(item: Api.IodataSections) =>
                this.props.editActions.setEdited({
                  ...editedEnumeration,
                  iodataSections: [...editedEnumeration.iodataSections, item]
                })
              }
              rows={uniqueDatasections}
              columnType="IO Data Sections Select"
            />
          </Modal.Content>
        </Modal>
      </Editor>
    );
  }

  private toggleModal = (): void => {
    this.setState(prevState => ({
      ...prevState,
      isModalOpen: !prevState.isModalOpen
    }));
  };

  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);

  private isFieldValid = (field: string): boolean => {
    const { editedEnumeration, pdApi } = this.props;
    if (!Number.isInteger(editedEnumeration.enumValue)) return false;
    switch (field) {
      case baseEnumFields("enumName"): {
        const { enumName } = editedEnumeration;
        if (!enumName || enumName.replace(/\s/g, "").length === 0) {
          return false;
        } else {
          return _.values(pdApi[this.props.dataFieldName]).find(
            enumeration =>
              enumeration.enumValue !== editedEnumeration.enumValue &&
              enumeration.enumName.trim() === editedEnumeration.enumName.trim()
          )
            ? false
            : true;
        }
      }

      case baseEnumFields("enumValue"):
        if (this.props.storedEnumeration) {
          return true;
        }
        return _.values(pdApi[this.props.dataFieldName]).find(
          enumeration => enumeration.enumValue === editedEnumeration.enumValue
        )
          ? false
          : true;

      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedEnumeration } = this.props;
    const temp = _.keys(editedEnumeration);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEnumeration: state.editView.editedObject,
  storedEnumeration: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditIoBasicDataType);


